import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  FileText,
  Eye,
  Edit,
  AlertTriangle,
  Loader2,
  Copy,
  KeyRound,
  Check,
  Trash2
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { useApi } from '../hooks/useApi';
import { tenantAPI } from '../services/api';
import AddTenantForm from '../components/forms/AddTenantForm';

const Tenants = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const { data: tenants, loading, error, refetch } = useApi(() => tenantAPI.getAll());

  const handleDeleteTenant = async (tenantId, tenantName) => {
    if (!window.confirm(`Are you sure you want to delete tenant "${tenantName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/${tenantId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
        }
      });

      if (response.ok) {
        alert('Tenant deleted successfully');
        refetch(); // Reload the tenants list
      } else {
        const errorData = await response.json();
        alert(`Failed to delete tenant: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      alert('Error deleting tenant. Please try again.');
    }
  };

  const handleCopy = async (text, field = 'id') => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      // Clear the copied state after 2 seconds
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      console.error('Modern clipboard API failed, trying fallback', e);
      
      // Fallback to execCommand method
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        
        if (success) {
          setCopiedField(field);
          // Clear the copied state after 2 seconds
          setTimeout(() => setCopiedField(null), 2000);
        } else {
          console.error('Fallback copy method also failed');
        }
      } catch (fallbackError) {
        console.error('Both copy methods failed', fallbackError);
      }
    }
  };

  const filteredTenants = tenants ? tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tenant.unit || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tenant.property_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading tenants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">Failed to load tenants</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const TenantCard = ({ tenant }) => (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={tenant.profile_image} alt={tenant.name} />
              <AvatarFallback>
                {tenant.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{tenant.name}</h3>
              <p className="text-sm text-gray-600">{tenant.property_name || 'Unknown Property'} - Unit {tenant.unit}</p>
              <p className="text-xs text-gray-500">{tenant.property_address || 'Unknown Address'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={getStatusColor(tenant.status)}>
              {tenant.status}
            </Badge>
            <Badge className={getStatusColor(tenant.payment_status)}>
              {tenant.payment_status}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">{tenant.email}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">{tenant.phone}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Monthly Rent</p>
            <p className="font-semibold text-lg">{formatCurrency(tenant.rent_amount)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Next Due</p>
            <p className="font-semibold">{formatDate(tenant.lease_start)}</p>
          </div>
        </div>

        {/* Login Credentials Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center mb-2">
            <KeyRound className="h-4 w-4 text-blue-600 mr-2" />
            <p className="text-sm font-semibold text-blue-800">Tenant Portal Credentials</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-blue-600 mb-1">Tenant ID:</p>
              <div className="flex items-center justify-between bg-white rounded px-2 py-1 border">
                <span className="font-mono text-gray-700 truncate">{tenant.id || tenant._id}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 ml-1"
                  onClick={() => handleCopy(tenant.id || tenant._id, `id-${tenant.id}`)}
                >
                  {copiedField === `id-${tenant.id}` ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <Copy className="h-3 w-3 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>
            <div>
              <p className="text-blue-600 mb-1">Access Code:</p>
              <div className="flex items-center justify-between bg-white rounded px-2 py-1 border">
                <span className="font-mono text-gray-700">{tenant.access_code || 'N/A'}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 ml-1"
                  onClick={() => handleCopy(tenant.access_code, `code-${tenant.id}`)}
                  disabled={!tenant.access_code}
                >
                  {copiedField === `code-${tenant.id}` ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <Copy className="h-3 w-3 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Lease: {formatDate(tenant.lease_start)} - {formatDate(tenant.lease_end)}</span>
          </div>
          <div className="flex space-x-1">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setSelectedTenant(tenant)}
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                setEditingTenant(tenant);
                setShowEditForm(true);
              }}
              title="Edit Tenant"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleDeleteTenant(tenant.id || tenant._id, tenant.name)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete Tenant"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-600 mt-1">Manage your tenant relationships</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAddForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tenants by name, email, or unit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline">All Tenants</Button>
              <Button variant="outline">Active</Button>
              <Button variant="outline">Overdue</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTenants.map((tenant) => (
          <TenantCard key={tenant.id} tenant={tenant} />
        ))}
      </div>

      {/* Tenant Details Modal/Sidebar */}
      {selectedTenant && (
        <Card className="fixed inset-y-0 right-0 w-96 shadow-xl z-50 bg-white overflow-y-auto">
          <CardHeader className="flex flex-row items-center justify-between border-b">
            <CardTitle>Tenant Details</CardTitle>
            <Button variant="ghost" onClick={() => setSelectedTenant(null)}>
              ×
            </Button>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Profile Section */}
            <div className="text-center">
              <Avatar className="h-20 w-20 mx-auto mb-4">
                <AvatarImage src={selectedTenant.profile_image} alt={selectedTenant.name} />
                <AvatarFallback className="text-lg">
                  {selectedTenant.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <h3 className="text-xl font-semibold text-gray-900">{selectedTenant.name}</h3>
              <p className="text-gray-600">{selectedTenant.propertyName || 'Unknown Property'} - Unit {selectedTenant.unit_number}</p>
            </div>

            {/* Login Credentials */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <KeyRound className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="font-semibold text-blue-800">Tenant Portal Login</h4>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-blue-700 mb-1 block">Tenant ID:</label>
                  <div className="flex items-center bg-white border rounded-md px-3 py-2">
                    <span className="font-mono text-sm flex-1 mr-2">{selectedTenant.id || selectedTenant._id}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => handleCopy(selectedTenant.id || selectedTenant._id, `modal-id-${selectedTenant.id}`)}
                    >
                      {copiedField === `modal-id-${selectedTenant.id}` ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-blue-700 mb-1 block">Access Code:</label>
                  <div className="flex items-center bg-white border rounded-md px-3 py-2">
                    <span className="font-mono text-sm flex-1 mr-2">{selectedTenant.access_code || 'N/A'}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => handleCopy(selectedTenant.access_code, `modal-code-${selectedTenant.id}`)}
                      disabled={!selectedTenant.access_code}
                    >
                      {copiedField === `modal-code-${selectedTenant.id}` ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
                  Share these credentials with your tenant to access their portal
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="font-semibold mb-3">Contact Information</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">{selectedTenant.email}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">{selectedTenant.phone}</span>
                </div>
              </div>
            </div>

            {/* Lease Information */}
            <div>
              <h4 className="font-semibold mb-3">Lease Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Rent:</span>
                  <span className="font-medium">{formatCurrency(selectedTenant.rent_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lease Start:</span>
                  <span>{formatDate(selectedTenant.lease_start)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lease End:</span>
                  <span>{formatDate(selectedTenant.lease_end)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Next Payment Due:</span>
                  <span>{formatDate(selectedTenant.lease_start)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Badge className={getStatusColor(selectedTenant.status)}>
                    {selectedTenant.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <Badge className={getStatusColor(selectedTenant.payment_status)}>
                    {selectedTenant.payment_status}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Documents */}
            {Array.isArray(selectedTenant.documents) && selectedTenant.documents.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">Documents</h4>
                <div className="space-y-2">
                  {selectedTenant.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{doc}</span>
                      </div>
                      <Button size="sm" variant="ghost">
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Tenant Form */}
      {showAddForm && (
        <AddTenantForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            refetch();
            setShowAddForm(false);
          }}
        />
      )}

      {/* Edit Tenant Form */}
      {showEditForm && editingTenant && (
        <AddTenantForm
          tenant={editingTenant}
          onClose={() => {
            setShowEditForm(false);
            setEditingTenant(null);
          }}
          onSuccess={() => {
            refetch();
            setShowEditForm(false);
            setEditingTenant(null);
          }}
        />
      )}
    </div>
  );
};

export default Tenants;